import useEventListener from '@use-it/event-listener';
import React, { useState } from 'react';
import { allow } from '../../common/classes/allow';
import ApiErrorAlert from '../../common/components/api.error.alert';
import Button from '../../common/components/button';
import Checkbox from '../../common/components/checkbox';
import DateFields from '../../common/components/date.fields';
import Div from '../../common/components/div';
import H1 from '../../common/components/h1';
import H2 from '../../common/components/h2';
import H3 from '../../common/components/h3';
import H5 from '../../common/components/h5';
import H6 from '../../common/components/h6';
import InputField from '../../common/components/input.field';
import MDAndLargerDiv from '../../common/components/md.and.larger.div';
import SecurityNoticeDialog from '../../common/components/security.notice.dialog';
import StackingButtons from '../../common/components/stacking.buttons';
import SuffixDropdown from '../../common/components/suffix.dropdown';
import TranslatedSpan from '../../common/components/translated.span';
import ValidationAlertMessage from '../../common/components/validation.alert.message';
import ValidationAlerts from '../../common/components/validation.alerts';
import XSAndSMDiv from '../../common/components/xs.and.sm.div';
import capitalizeFirstLetter from '../../common/functions/capitalize.first.letter';
import { isValidYear } from '../../common/functions/is.valid.year';
import translate from '../../common/functions/translate';
import { useConversion, useFormStart } from '../../common/hooks/use.analytics';
import useTrait from '../../common/hooks/use.trait';
import is from '../../common/objects/is';
import dateFieldValue from '../../common/objects/models/date.field.value';
import use from '../../common/objects/use';

const YourBasicInformation = () => {
    const [certificationCheckedRef, setCertificationCheckedRef] = useState();
    const [confirmEmailAddressRef, setConfirmEmailAddressRef] = useState();
    const [dayRef, setDayRef] = useState();
    const [emailAddressRef, setEmailAddressRef] = useState();
    const [lastNameRef, setLastNameRef] = useState();
    const [monthRef, setMonthRef] = useState();
    const [yearRef, setYearRef] = useState();
    const [zipCodeRef, setZipCodeRef] = useState();
    const apiErrorMessage = useTrait(null);
    const certificationCheckedError = useTrait(false);
    const confirmEmailAddressError = useTrait(false);
    const dateFieldKey = useTrait(new Date().getTime());
    const emailAddressError = useTrait(false);
    const lastNameError = useTrait(false);
    const validateEntireForm = useTrait(false);
    const zipCodeOrCityError = useTrait(false);
    const [isLoading, setIsloading] = useState(false);
   
    const api = use.createAccountApi;
    const data = use.createAccountData;
    const day = translate('day');
    const month = translate('month');
    const year = translate('year');

    const formStart = useFormStart('sign_up');
    const conversion = useConversion('create account', 'sign_up');
   
    const callCreateAccount = async () => {
        const lastNameWithSuffix = data.suffix.get() ? `${data.lastName.get()} ${data.suffix.get()}` : data.lastName.get();
        const response = await api.step2(lastNameWithSuffix, data.dateOfBirth.get(), data.zipCodeOrCity.get(), data.emailAddress.get());
        setIsloading(false);
        const statusCode = response?.status;
        validateEntireForm.set(false);
        dateFieldKey.set(new Date().getTime());

        let success = false;
        switch (statusCode) {
                case 204:
                    success = true;
                    data.stepForward();
                    break;
                case 400:
                    reset();
                    const isNotVerified = response?.data?.errors?.some(error => error.message?.includes('not verified'));
                    if (isNotVerified)
                        apiErrorMessage.set('We’re not able to find a matching record for the information you provided. Please check the information and try again.');
                    else
                        apiErrorMessage.set('We couldn\'t process your request. Please re-enter your information and try again.');
                    break;
                case 404:
                    reset();
                    apiErrorMessage.set('We couldn\'t process your request. Please re-enter your information and try again.');
                    break;
                case 409:
                    reset();
                    apiErrorMessage.set('It looks like you already have an account.');
                    break;
                default:
                    reset();
                    apiErrorMessage.set('We can\'t process your request at this time. We\'re sorry for any inconvenience. Please try again.');
                    break;
        }
   
        if (!success) {
            const errorMessage = apiErrorMessage.get() || 'unkown error';
            conversion.failAPIIntermediate(errorMessage);
        }
    };
   
    const getApiErrorMessage = () => {
        if (!apiErrorMessage.get())
            return null;
        return translate(apiErrorMessage.get());
    };
   
    const getBackButton = () => {
        return <>
            <Button
                color={'teal'}
                displayAs={'button'}
                fill={'outlined'}
                onClick={data.stepBackward}
                size={'small'}
            >
                <TranslatedSpan english={'Back'}/>
            </Button>
        </>;
    };
   
    const getLastNameInputField = () => {
        return <>
            <InputField
                aria-invalid={(!!lastNameError.get()).toString()}
                errorMessage={lastNameError.get() ? `${translate('Error')}: ${translate('Enter your last name.')}` : null}
                inputRef={setLastNameRef}
                label={translate('Last name')}
                name={'lastName'}
                onClick={formStart}
                onBlur={event => updateLastName(event.currentTarget.value, is.to.be.validated)}
                onChange={event => updateLastName(event.currentTarget.value)}
                style={{minWidth: '100%'}}
                value={data.lastName.get()}
            />
        </>;
    };
   
    const getNextButton = () => {
        return <>
            <Button
                color={'teal'}
                displayAs={'button'}
                fill={'solid'}
                onClick={handleNextButtonClick}
                size={'small'}
                isLoading={isLoading}
            >
                <TranslatedSpan english={'Next'}/>
            </Button>
        </>;
    };
   
    const getSuffixDropdown = () => {
        return <>
            <SuffixDropdown
                onChange={event => data.suffix.set(event.currentTarget.value)}
                style={{
                    paddingLeft: 8,
                    paddingRight: 28,
                }}
                value={data.suffix.get()}
            />
        </>;
    };
   
    const getValidationAlertMessages = () => {
        const messages = [];
        if (!validateEntireForm.get())
            return messages;
        if (data.lastName.get() === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter your last name.')}
                    fieldReference={lastNameRef}
                    linkText={translate('Last name')}
                />
            );
        if (data.dateOfBirth.get().month === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter the {{timePeriod}} you were born.', {timePeriod: month})}
                    fieldReference={monthRef}
                    linkText={capitalizeFirstLetter(translate('month'))}
                />
            );
        if (data.dateOfBirth.get().day === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter the {{timePeriod}} you were born.', {timePeriod: day})}
                    fieldReference={dayRef}
                    linkText={capitalizeFirstLetter(translate('day'))}
                />
            );
        if (!isValidYear(data.dateOfBirth.get().year))
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter the {{timePeriod}} you were born.', {timePeriod: year})}
                    fieldReference={yearRef}
                    linkText={capitalizeFirstLetter(translate('year'))}
                />
            );
        if (data.zipCodeOrCity.get() === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter your ZIP code or city.')}
                    fieldReference={zipCodeRef}
                    linkText={translate('ZIP code or city')}
                />
            );
        if (data.emailAddress.get() === '' && !data.noEmailAddressChecked.get())
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter your email address or select "I don\'t have an email address".')}
                    fieldReference={emailAddressRef}
                    linkText={translate('Email address')}
                />
            );
        if (data.confirmEmailAddress.get() !== data.emailAddress.get() && !data.noEmailAddressChecked.get())
            messages.push(
                <ValidationAlertMessage
                    description={translate('Confirm your email address or select "I don\'t have an email address".')}
                    fieldReference={confirmEmailAddressRef}
                    linkText={translate('Confirm email address')}
                />
            );
        if (!data.certificationChecked.get())
            messages.push(
                <ValidationAlertMessage
                    description={translate('Please select the Certification Statement checkbox to continue.')}
                    fieldReference={certificationCheckedRef}
                    linkText={translate('Certification statement')}
                />
            );
        return messages;
    };
   
    const handleNextButtonClick = () => {
        if (isLoading) {
            return;
        }
        setIsloading(true);
   
        conversion.attempt();
        const errorFieldRef = validateForm();
        if (errorFieldRef !== null) {
            errorFieldRef.focus();
            setIsloading(false);
            return;
        }
        callCreateAccount();
    };
   
    const reset = () => {
        apiErrorMessage.set(null);
        certificationCheckedError.set(false);
        confirmEmailAddressError.set(false);
        emailAddressError.set(false);
        zipCodeOrCityError.set(false);
    };
   
    const updateCertificationChecked = (event = {}) => {
        allow.anObject(event);
        formStart();
        if (event.type === 'change')
            data.certificationChecked.set(!data.certificationChecked.get());
        validateCertificationChecked();
        if (validateEntireForm.get())
            validateForm();
    };
   
    const updateConfirmEmailAddress = (value = '', doImmediateValidation = false) => {
        allow.aString(value).aBoolean(doImmediateValidation);
        formStart();
        const newValue = value.trimStart();
        data.confirmEmailAddress.set(newValue);
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateConfirmEmailAddress();
        if (validateEntireForm.get())
            validateForm();
    };
   
    const updateDateOfBirth = (dateObject = dateFieldValue) => {
        allow.anInstanceOf(dateObject, dateFieldValue);
        formStart();
        data.dateOfBirth.set(dateObject);
        if (validateEntireForm.get())
            validateForm();
    };
   
    const updateEmailAddress = (value = '', doImmediateValidation = false) => {
        allow.aString(value).aBoolean(doImmediateValidation);
        formStart();
        const newValue = value.trimStart();
        data.emailAddress.set(newValue);
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateEmailAddress();
        if (validateEntireForm.get())
            validateForm();
    };
   
    const updateLastName = (value = '', doImmediateValidation = false) => {
        allow.aString(value).aBoolean(doImmediateValidation);
        formStart();
        const newValue = value.trimStart();
        data.lastName.set(newValue);
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateLastName();
        if (validateEntireForm.get())
            validateForm();
    };
   
    const updateNoEmailAddressChecked = (event = {}) => {
        allow.anObject(event);
        formStart();
        if (event.type === 'change') {
            if (!data.noEmailAddressChecked.get()) {
                if (data.emailAddress.get() !== '')
                    data.emailAddress.set('');
                if (emailAddressError.get() !== null)
                    emailAddressError.set(null);
                if (data.confirmEmailAddress.get() !== '')
                    data.confirmEmailAddress.set('');
                if (confirmEmailAddressError.get() !== null)
                    confirmEmailAddressError.set(null);
            }
            data.noEmailAddressChecked.set(!data.noEmailAddressChecked.get());
        }
        if (validateEntireForm.get())
            validateForm();
    };
   
    const updateZipCode = (value = '', doImmediateValidation = false) => {
        allow.aString(value).aBoolean(doImmediateValidation);
        formStart();
        data.zipCodeOrCity.set(value);
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateZipCode();
        if (validateEntireForm.get())
            validateForm();
    };
   
    const validateCertificationChecked = () => {
        const isValid = data.certificationChecked.get();
        if (certificationCheckedError.get() === isValid)
            certificationCheckedError.set(!isValid);
        return isValid;
    };
   
    const validateConfirmEmailAddress = () => {
        const isValid = data.noEmailAddressChecked.get() || (data.confirmEmailAddress.get() !== '' && data.confirmEmailAddress.get() === data.emailAddress.get());
        if (confirmEmailAddressError.get() === isValid)
            confirmEmailAddressError.set(!isValid);
        return isValid;
    };
   
    const validateEmailAddress = () => {
        const isValid = data.noEmailAddressChecked.get() || data.emailAddress.get() !== '';
        if (emailAddressError.get() === isValid)
            emailAddressError.set(!isValid);
        return isValid;
    };
   
    const validateForm = () => {
        if (!validateEntireForm.get())
            validateEntireForm.set(true);
        let errorFieldRef = null;
        if (!validateLastName())
            errorFieldRef = lastNameRef;
        if (data.dateOfBirth.get().month === '' && !errorFieldRef)
            errorFieldRef = monthRef;
        if (data.dateOfBirth.get().day === '' && !errorFieldRef)
            errorFieldRef = dayRef;
        if (!isValidYear(data.dateOfBirth.get().year) && !errorFieldRef)
            errorFieldRef = yearRef;
        if (!validateZipCode() && !errorFieldRef)
            errorFieldRef = zipCodeRef;
        if (!validateEmailAddress() && !errorFieldRef)
            errorFieldRef = emailAddressRef;
        if (!validateConfirmEmailAddress() && !errorFieldRef)
            errorFieldRef = confirmEmailAddressRef;
        if (!validateCertificationChecked() && !errorFieldRef)
            errorFieldRef = certificationCheckedRef;
        const messages = getValidationAlertMessages();
        if (messages.length) {
            const errorString = messages.map(x => x.props.description).join(', ');
            conversion.failValidationIntermediate(errorString);
        }
        return messages.length ? errorFieldRef : null;
    };
   
    const validateLastName = () => {
        const isValid = data.lastName.get() !== '';
        if (lastNameError.get() === isValid)
            lastNameError.set(!isValid);
        return isValid;
    };
   
    const validateZipCode = () => {
        const isValid = data.zipCodeOrCity.get() !== '';
        if (zipCodeOrCityError.get() === isValid)
            zipCodeOrCityError.set(!isValid);
        return isValid;
    };
   
    useEventListener('keyup', event => {
        if (event.key === 'Enter')
            handleNextButtonClick();
    });
   
    return <>
        <SecurityNoticeDialog/>
        <fieldset style={{
            border: 0,
            margin: 0,
            padding: 0,
        }}>
            <Div isRow={true}>
                <Div cols={12}>
                    <H1>
                        <TranslatedSpan english={'Create an account'}/>
                    </H1>
                    <ApiErrorAlert errorMessage={getApiErrorMessage()}/>
                    <legend>
                        <Div paddingTopSM={1} paddingTopMD={2} paddingTopLG={2} paddingTopXL={4}>
                            <H6>
                                <TranslatedSpan english={'Step'}/> 2 <TranslatedSpan english={'of'}/> 3
                            </H6>
                        </Div>
                        <H2 style={{marginTop: 8}}>
                            <TranslatedSpan english={'Your basic information'}/>
                        </H2>
                    </legend>
                    <ValidationAlerts
                        heading={`${translate('Error')}: ${translate('Complete these items to sign up for an account.')}`}
                        validationMessages={getValidationAlertMessages()}
                    />
                </Div>
            </Div>
            <XSAndSMDiv>
                <Div isRow={true}>
                    <Div cols={12}>
                        {getLastNameInputField()}
                    </Div>
                </Div>
                <Div isRow={true}>
                    <Div cols={8}>
                        {getSuffixDropdown()}
                    </Div>
                </Div>
            </XSAndSMDiv>
            <MDAndLargerDiv>
                <Div isRow={true}>
                    <Div
                        colsMD={6} colsLG={5} colsXL={4}
                        paddingRight={1}
                    >
                        {getLastNameInputField()}
                    </Div>
                    <Div
                        cols={2} colsLG={1}
                        paddingLeft={0} paddingRightLG={0}
                    >
                        {getSuffixDropdown()}
                    </Div>
                </Div>
            </MDAndLargerDiv>
            <Div isRow={true}>
                <Div cols={12}>
                    <DateFields
                        date={data.dateOfBirth.get()}
                        dayError={`${translate('Error')}: ${translate('Enter the {{timePeriod}} you were born.', {timePeriod: day})}`}
                        dayFieldRef={setDayRef}
                        forceValidation={validateEntireForm.get()}
                        hint={translate('Use the format MM/DD/YYYY')}
                        key={dateFieldKey.get()}
                        label={translate('Date of Birth')}
                        monthError={`${translate('Error')}: ${translate('Enter the {{timePeriod}} you were born.', {timePeriod: month})}`}
                        monthFieldRef={setMonthRef}
                        onBlur={date => updateDateOfBirth(date)}
                        onChange={date => updateDateOfBirth(date)}
                        yearError={`${translate('Error')}: ${translate('Enter the {{timePeriod}} you were born.', {timePeriod: year})}`}
                        yearFieldRef={setYearRef}
                    />
                </Div>
            </Div>
            <Div isRow={true}>
                <Div
                    cols={12} colsSM={10} colsMD={7} colsLG={4} colsXL={3}
                    paddingRight={1}
                >
                    <InputField
                        aria-invalid={(!!zipCodeOrCityError.get()).toString()}
                        errorMessage={zipCodeOrCityError.get() ? `${translate('Error')}: ${translate('Enter your ZIP code or city.')}` : null}
                        inputRef={setZipCodeRef}
                        label={translate('ZIP code or city')}
                        name={'zipCode'}
                        onClick={formStart}
                        onBlur={event => updateZipCode(event.currentTarget.value.trim(), is.to.be.validated)}
                        onChange={event => updateZipCode(event.currentTarget.value)}
                        style={{minWidth: '100%'}}
                        value={data.zipCodeOrCity.get()}
                    />
                </Div>
            </Div>
            <Div isRow={true}>
                <Div
                    cols={12} colsSM={12} colsMD={8} colsLG={6} colsXL={5}
                    paddingRight={1}
                >
                    <InputField
                        aria-invalid={(!!emailAddressError.get()).toString()}
                        disabled={!!data.noEmailAddressChecked.get()}
                        errorMessage={emailAddressError.get() ? `${translate('Error')}: ${translate('Enter your email address or select "I don\'t have an email address".')}` : null}
                        inputRef={setEmailAddressRef}
                        label={translate('Email address')}
                        name={'emailAddress'}
                        onClick={formStart}
                        onBlur={event => updateEmailAddress(event.currentTarget.value, is.to.be.validated)}
                        onChange={event => updateEmailAddress(event.currentTarget.value)}
                        style={{minWidth: '100%'}}
                        value={data.emailAddress.get()}
                    />
                </Div>
            </Div>
            <Div isRow={true}>
                <Div
                    cols={12} colsSM={12} colsMD={8} colsLG={6} colsXL={5}
                    paddingRight={1}
                >
                    <InputField
                        aria-invalid={(!!confirmEmailAddressError.get()).toString()}
                        disabled={!!data.noEmailAddressChecked.get()}
                        errorMessage={confirmEmailAddressError.get() ? `${translate('Error')}: ${translate('Confirm your email address or select "I don\'t have an email address".')}` : null}
                        inputRef={setConfirmEmailAddressRef}
                        label={translate('Confirm email address')}
                        name={'confirmEmailAddress'}
                        onClick={formStart}
                        onBlur={event => updateConfirmEmailAddress(event.currentTarget.value, is.to.be.validated)}
                        onChange={event => updateConfirmEmailAddress(event.currentTarget.value)}
                        style={{minWidth: '100%'}}
                        value={data.confirmEmailAddress.get()}
                    />
                </Div>
            </Div>
            <Div isRow={true}>
                <Div
                    cols={12}
                    paddingTop={2}
                >
                    <H5>
                        <TranslatedSpan english={'Don\'t have an email address?'}/>
                    </H5>
                    <p style={{
                        marginBottom: 0,
                        marginTop: 8,
                    }}>
                        <TranslatedSpan english={'We\'ll send information about your account by mail to the address on file.'}/>
                    </p>
                    <Checkbox
                        checked={!!data.noEmailAddressChecked.get()}
                        className={'ds-u-margin-top--0'}
                        name={'noEmailAddressCheckbox'}
                        onBlur={event => updateNoEmailAddressChecked(event)}
                        onChange={event => updateNoEmailAddressChecked(event)}
                        title={translate('I don\'t have an email address.')}
                        type={'checkbox'}
                        value={'noEmailAddress'}
                    >
                    </Checkbox>
                </Div>
            </Div>
            <Div isRow={true}>
                <Div
                    cols={12}
                    paddingTop={2}
                >
                    <H3>
                        <TranslatedSpan english={'Agree to the statements below'}/>
                    </H3>
                </Div>
            </Div>
            <Div isRow={true}>
                <Div
                    cols={12}
                    paddingTop={2}
                >
                    <Checkbox
                        aria-invalid={(!!certificationCheckedError.get()).toString()}
                        checked={data.certificationChecked.get()}
                        className={'ds-u-margin-top--0'}
                        errorMessage={certificationCheckedError.get() ? `${translate('Error')}: ${translate('Please select the Certification Statement checkbox to continue.')}` : null}
                        inputRef={setCertificationCheckedRef}
                        name={'certificationCheckbox'}
                        onBlur={event => updateCertificationChecked(event)}
                        onChange={event => updateCertificationChecked(event)}
                        title={translate('By checking this box, you certify that the information listed is true and complete to the best of your knowledge.')}
                        type={'checkbox'}
                        value={'certified'}
                    >
                    </Checkbox>
                </Div>
            </Div>
        </fieldset>
        <StackingButtons
            button1={getBackButton()}
            button2={getNextButton()}
        />
    </>;
}

export default YourBasicInformation;